import { useMemo } from "react";
import { useQuery } from "react-query";
import { usePlatformAPI } from "../PlatformProvider";
import { createUserApi } from "./users";
import type { Result } from "../PlatformClient";

function unwrap<T>(result: Result<T>): T {
    const [data, err] = result;
    if (err) throw err;
    return data as T;
}

export function useUserApi() {
    const client = usePlatformAPI();
    return useMemo(() => createUserApi(client), [client]);
}

export const userKeys = {
    all: ["users"] as const,
    list: (accessLevel: string, search?: string, excludeIDs?: string[]) =>
        [...userKeys.all, "list", accessLevel, search ?? "", ...(excludeIDs ?? [])] as const,
    guardianDepartments: (id: string) => [...userKeys.all, id, "guardian-departments"] as const,
};

export function useUsers(accessLevel: string, search?: string, excludeIDs?: string[]) {
    const api = useUserApi();
    return useQuery(
        userKeys.list(accessLevel, search, excludeIDs),
        () => api.list(accessLevel, search, excludeIDs).then(unwrap),
        { enabled: !!accessLevel },
    );
}

export function useGuardianDepartments(userID: string) {
    const api = useUserApi();
    return useQuery(
        userKeys.guardianDepartments(userID),
        () => api.guardianDepartments(userID).then(unwrap),
        { enabled: !!userID },
    );
}
